import React from 'react';
import PropTypes from 'prop-types';
import {reduxForm} from 'redux-form';
import {createLabelHelper} from 'infrastructure/js/utils/labelHelper';
import Grid from 'infrastructure/js/components/Grid/gridWrapper.js';
import ValueWithStateCell from '../../../Common/CustomGridCells/ValueWithStateCell/valueWithStateCell.js';
import LengthCell from '../../../Common/CustomGridCells/LengthCell/lengthCell.js';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper.js';
import LabelWithTooltipCell from '../../../Common/CustomGridCells/LabelWithTooltipCell/labelWithTooltipCell.js';
import {navigationStates} from '../../../../enums/navigationStates';
import {filterTypes} from 'infrastructure/js/enums/filterTypes.js';
import {gridsNames} from '../../../../enums/gridsNames';
import {FetchEntitiesFilters} from '../../../../enums/fetchEntitiesFilters';
import {EntityPropertyTypes} from '../../../../enums/entityPropertyTypes';
import MaterialHelper from '../../../../utils/materialHelper';
import {getAssetEtl} from '../../../../utils/assetHelper';
import EntityIdCell from '../../../Common/CustomGridCells/EntityIdCell/entityIdCell';
import DefrostingLabelCell from '../../../Common/CustomGridCells/DefrostingLabelCell/defrostingLabelCell';
import GridHelper from 'infrastructure/js/components/Grid/Utils/gridHelper';
import WidthCell from '../../../Common/CustomGridCells/WidthCell/widthCell';
import EntityAlertIdCell from '../../../Common/CustomGridCells/EntityAlertIdCell/entityAlertIdCell';

require('./spoolingGrid.scss');

class SpoolingGrid extends React.PureComponent {

  filterConfig = [
    {fieldName: 'businessId', filterName: 'businessId', getOptions: false},
    {fieldName: 'material.businessId', filterName: 'material', getOptions: false},
    {fieldName: 'defrostingStatus', filterName: 'defrostingStatus', getOptions: true},
    {fieldName: 'lot', filterName: 'lot', getOptions: false},
    {fieldName: 'expirationDate', filterName: 'assetExpirationDate', getOptions: false},
    {fieldName: 'exposureTimeLeft', filterName: 'assetCalculatedEtl', getOptions: false},
    {fieldName: 'lengthLeft', filterName: 'length'},
    {fieldName: 'widthInCentimeter', filterName: 'width'},
  ];

  constructor(props) {
    super(props);
    this.labels = createLabelHelper('mat.grid.');
    this.allLabels = createLabelHelper('');
    this.filterDefrostinglabels = createLabelHelper('mat.filter.defrosting.');
    this.etlLabels = createLabelHelper('enum.etl.type.');

    this.columnsConfig = this.createColumnsConfig();
  }

  createColumnsConfig = () => {

    let columnsConfig = [
      {
        fieldName: 'businessId',
        title: this.labels.get('columns.cassetteId.title'),
        filterType: filterTypes.MULTI_SELECT_ASYNC,
        filterName: 'businessId',
        fetchConfig: {
          entityType: EntityPropertyTypes.CASSETTE_BUSINESS_ID,
          filter: [FetchEntitiesFilters.ACTIVE, FetchEntitiesFilters.ON_LOCATION],
          action: this.props.actions.fetchEntities
        },
        columnOptions: {
          cellComponent: EntityAlertIdCell,
          valueGetter: (params) => {
            return {
              id: params.data.id,
              label: params.data.businessId,
              type: navigationStates.CASSETTE,
              alerts: params.data.alerts,
              alertStatus: params.data.alertStatus,
              exposureTimeLeftBond: params.data.exposureTimeLeftBond,
              exposureTimeLeftCure: params.data.exposureTimeLeftCure,
              expirationDaysLeft: params.data.m_ExpirationDaysLeft,
              defrostingTimeLeft: params.data.defrostingTimeLeft
            };
          }
        }
      },
      {
        fieldName: 'material.businessId',
        title: this.labels.get('columns.material.title'),
        filterType: filterTypes.MULTI_SELECT_ASYNC,
        filterName: 'material',
        fetchConfig: {
          entityType: EntityPropertyTypes.MATERIAL_TYPE_NAME_AND_BUSINESS_ID,
          filter: [FetchEntitiesFilters.ASSET_ACTIVE, FetchEntitiesFilters.ASSET_TYPE_CASSETTE, FetchEntitiesFilters.ON_LOCATION],
          action: this.props.actions.fetchEntities
        },
        columnOptions: {
          cellComponent: LabelWithTooltipCell,
          valueGetter: (params) => {
            return MaterialHelper.getMaterialFullLabel(params.data.material.materialName, params.data.material.businessId);
          }
        }
      },
      // ...(this.getLengthOrWeight()),
      {
        fieldName: 'lengthLeft',
        title: this.labels.get('columns.length.title'),
        filterType: filterTypes.NONE,
        width: 110,
        columnOptions: {
          cellComponent: LengthCell,
          valueGetter: (params) => {
            return {
              length: params.data.lengthLeft
            };
          },
        }
      },
      {
        fieldName: 'widthInCentimeter',
        title: this.labels.get('columns.width.title'),
        filterType: filterTypes.NONE,
        width: 110,
        columnOptions: {
          cellComponent: WidthCell,
          valueGetter: (params) => {
            return {
              width: params.data.widthInCentimeter
            };
          },
        }
      },
      {
        fieldName: 'defrostingStatus',
        title: this.labels.get('columns.defrosting.title'),
        filterType: filterTypes.MULTI_SECTIONS,
        filterSections: this.getDefrostingColumnFilterSections(),
        filterName: 'defrostingStatus',
        width: 170,
        columnOptions: {
          cellComponent: DefrostingLabelCell,
          valueGetter: (params) => {
            return {
              status: params.data.defrostingStatus,
              statusLabel: params.data.defrostingStatusDisplayKey ? this.allLabels.get(params.data.defrostingStatusDisplayKey) : '',
              defrostingTimeLeft: params.data.defrostingTimeLeft
            }
          }
        }
      },
      {
        fieldName: 'lot',
        title: this.labels.get('columns.lot.title'),
        filterType: filterTypes.MULTI_SELECT_ASYNC,
        filterName: 'lot',
        fetchConfig: {
          entityType: EntityPropertyTypes.LOT_NAME,
          filter: [FetchEntitiesFilters.ASSET_ACTIVE, FetchEntitiesFilters.ASSET_TYPE_CASSETTE, FetchEntitiesFilters.ON_LOCATION],
          action: this.props.actions.fetchEntities
        },
        columnOptions: {
          cellComponent: EntityIdCell,
          valueGetter: (params) => ({
            id: params.data.lot?.id,
            label: params.data.lot?.name,
            type: params.data.lot ? navigationStates.LOT : '',
          }),
        },
      },
      {
        fieldName: 'expirationDate',
        title: this.labels.get('columns.expiration.title'),
        filterType: filterTypes.NONE,
        width: 150,
        columnOptions: {
          cellComponent: ValueWithStateCell,
          valueGetter: (params) => {
            if (params.data.expirationDate && params.data.expirationDate.value) {
              return {
                value: DateTimeHelper.FormatDateObjectToDayMonth(params.data.expirationDate.value),
                state: params.data.expirationDate.state
              };
            }

            return {value: '', state: ''};
          },
          headerComponentParams: (GridHelper.getDateFormatHeaderIcon())
        }
      },
      {
        fieldName: 'exposureTimeLeft',
        title: this.labels.get('columns.etl.title'),
        filterType: filterTypes.NONE,
        width: 160,
        columnOptions: {
          cellComponent: ValueWithStateCell,
          valueGetter: (params) => {
            return getAssetEtl(params.data, this.etlLabels);
          },
        }
      },

    ];
    return columnsConfig;
  }

  // getLengthOrWeight = () => {
  //
  //   if(PermissionManager.isWeightSupported()){
  //     return [
  //       {
  //         fieldName: 'weight',
  //         title: this.labels.get('columns.weight.title'),
  //         filterType: filterTypes.NONE,
  //         width: 110,
  //         columnOptions: {
  //           cellComponent: WeightCell,
  //           valueGetter: (params) => {
  //             return {
  //               weight: params.data.weight
  //             };
  //           },
  //         }
  //       }
  //     ]
  //   }
  //
  //   return [
  //     {
  //       fieldName: 'lengthLeft',
  //       title: this.labels.get('columns.length.title'),
  //       filterType: filterTypes.NONE,
  //       width: 110,
  //       columnOptions: {
  //         cellComponent: LengthCell,
  //         valueGetter: (params) => {
  //           return {
  //             length: params.data.lengthLeft
  //           };
  //         },
  //       }
  //     },
  //     {
  //       fieldName: 'widthInCentimeter',
  //       title: this.labels.get('columns.width.title'),
  //       filterType: filterTypes.NONE,
  //       width: 110,
  //       columnOptions: {
  //         cellComponent: WidthCell,
  //         valueGetter: (params) => {
  //           return {
  //             width: params.data.widthInCentimeter
  //           };
  //         },
  //       }
  //     }
  //   ]
  // };

  getDefrostingColumnFilterSections() {
    return [
      {
        properties: [
          {name: 'BEING_DEFROSTED', label: this.filterDefrostinglabels.get('beingDefrosted'), value: false},
          {name: 'DEFROSTED', label:  this.filterDefrostinglabels.get('defrosted'), value: false}
        ]
      },
    ];
  }

  render() {
    return (
      <div className="spooling-grid">
        <Grid gridName={gridsNames.LOCATION_SPOOLING}
              columnsConfig={this.columnsConfig}
              actions={this.props.actions}
              filterConfig={this.filterConfig}
              gridProps={
                {
                  getRowHeight: () => 84
                }
              }
        >
        </Grid>
      </div>
    );
  }
}

export default reduxForm({
    form: 'spoolingGrid'
  }
)(SpoolingGrid);

SpoolingGrid.propTypes = {
  actions: PropTypes.object.isRequired,
};
